import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaPhone, FaWhatsapp, FaCalendarAlt, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const FloatingBarContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999;
  display: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;

const ContactBar = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-top: 3px solid ${({ theme }) => theme.colors.primaryDark};
`;

const ContactButton = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${({ theme }) => theme.colors.dark};
  text-decoration: none;
  padding: ${({ theme }) => theme.spacing.xs};
  
  svg {
    font-size: 1.5rem;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    color: ${({ theme }) => theme.colors.secondary};
  }
  
  span {
    font-size: ${({ theme }) => theme.fontSizes.small};
    font-weight: 500;
  }
`;

const PhoneButton = styled(ContactButton)`
  &:hover svg {
    color: #28A745;
  }
`;

const WhatsappButton = styled(ContactButton)`
  &:hover svg {
    color: #25D366;
  }
`;

const BookingButton = styled(ContactButton)`
  &:hover svg {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const QuickContactButton = styled.button`
  position: fixed;
  bottom: 80px;
  right: 20px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primaryDark};
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  z-index: 1000;
  font-size: 1.5rem;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const QuickContactPanel = styled(motion.div)`
  position: fixed;
  bottom: 150px;
  right: 20px;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  padding: ${({ theme }) => theme.spacing.md};
  z-index: 1000;
  width: 200px;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  padding-bottom: ${({ theme }) => theme.spacing.xs};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray};
  
  h4 {
    margin: 0;
    color: ${({ theme }) => theme.colors.secondary};
  }
  
  button {
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.dark};
    cursor: pointer;
    font-size: 1rem;
    padding: 0;
  }
`;

const PanelItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  color: ${({ theme }) => theme.colors.dark};
  text-decoration: none;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing.sm};
    color: ${({ theme }) => theme.colors.secondary};
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
    
    svg {
      color: ${({ theme }) => theme.colors.primaryDark};
    }
  }
`;

const FloatingContactBar = () => {
  const [quickContactOpen, setQuickContactOpen] = useState(false);
  
  const toggleQuickContact = () => {
    setQuickContactOpen(!quickContactOpen);
  };
  
  return (
    <>
      <FloatingBarContainer>
        <ContactBar>
          <PhoneButton to="tel:+918360931465">
            <FaPhone />
            <span>Call</span>
          </PhoneButton>

          <WhatsappButton to="https://wa.me/918360931465" target="_blank" rel="noopener noreferrer">
            <FaWhatsapp />
            <span>WhatsApp</span>
          </WhatsappButton>
          
          <BookingButton to="/booking">
            <FaCalendarAlt />
            <span>Book Now</span>
          </BookingButton>
        </ContactBar>
      </FloatingBarContainer>
      
      <QuickContactButton onClick={toggleQuickContact}>
        {quickContactOpen ? <FaTimes /> : <FaPhone />}
      </QuickContactButton>
      
      <AnimatePresence>
        {quickContactOpen && (
          <QuickContactPanel
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
          >
            <PanelHeader>
              <h4>Contact Us</h4>
              <button onClick={toggleQuickContact}>
                <FaTimes />
              </button>
            </PanelHeader>
            
            <PanelItem to="tel:+918360931465">
              <FaPhone />
              <span>+91 8360931465</span>
            </PanelItem>

            <PanelItem to="https://wa.me/918360931465" target="_blank" rel="noopener noreferrer">
              <FaWhatsapp />
              <span>WhatsApp</span>
            </PanelItem>
            
            <PanelItem to="/booking">
              <FaCalendarAlt />
              <span>Book Appointment</span>
            </PanelItem>
          </QuickContactPanel>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingContactBar;
